import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(apiError(err, "Falha ao entrar. Verifique suas credenciais."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-background" data-testid="login-page">
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-emerald-600 text-white">
              <Dumbbell className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-xl font-bold tracking-tight leading-none">
                MUSCLE<span className="text-emerald-500">FACTORY</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Gestão de caixa</div>
            </div>
          </div>
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Bem-vindo de volta</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Entre para abrir o caixa e registrar vendas.
            </p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                required
                autoFocus
                data-testid="login-email"
                placeholder="voce@musclefactory.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Senha</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  required
                  data-testid="login-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label="Mostrar senha"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
                {error}
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="h-11 w-full bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Entrar
            </Button>
          </form>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1520615893238-4b864f6d16b7?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Muscle Factory"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="font-display text-4xl font-bold tracking-tight leading-tight">
            Caixa sob controle,<br />treino sem pausa.
          </div>
          <p className="mt-3 max-w-md text-sm text-white/70">
            Registre vendas em segundos, acompanhe o fluxo de caixa em tempo real e feche o dia com conferência automática.
          </p>
        </div>
      </div>
    </div>
  );
}
